import React, { useEffect, useMemo } from "react";
import { Button, Card, Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import { useState } from "react";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import debounce from "lodash.debounce";
import CustomSearchInput from "../../common/CustomSearchInput";
import NoRecords from "../../common/NoRecords";
import DataTable from "react-data-table-component";
import Loader from "../../common/Loader";
import { DEBOUNCE_TIME, DEFAULT_ROW_PER_PAGE } from "../../../helpers/constants";
import ActionButton from "../../common/ActionButton";
import { createBidRequest, getBidRequests, updateBidRequestStatus } from "../../../store/auctions/actions";
import { getCustomerList } from "../../../store/customers/actions";
import { useParams } from "react-router-dom";
import CustomTagChip from "../../common/CustomTagChip";
import { getCustomerOptionLabel, getOnlyCustomerName } from "../../../helpers/utils";

const BidRequestsModal = ({ isModalOpen, onCancel, t }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [tableData, setTableData] = useState({ data: [], total: 0 });
  // loader
  const [tableLoader, setTableLoader] = useState(false);

  const [payload, setPayload] = useState({
    page: 1,
    limit: DEFAULT_ROW_PER_PAGE,
    filter: { auction: id },
  });

  // invite customer — search + selection
  const [inviteCustomer, setInviteCustomer] = useState(null);
  const [inviteLoader, setInviteLoader] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchLoader, setCustomerSearchLoader] = useState(false);

  const handleCustomerSearch = useCallback((search) => {
    setCustomerSearchLoader(true);
    dispatch(getCustomerList({ page: 1, limit: DEFAULT_ROW_PER_PAGE, search }, true))
      .then((res) => {
        setCustomerOptions(res?.results || []);
      })
      .finally(() => setCustomerSearchLoader(false));
  }, [dispatch]);
  // debounce for customer search
  const customerSearchDebounce = useMemo(() => debounce(handleCustomerSearch, DEBOUNCE_TIME), [handleCustomerSearch]);

  useEffect(() => {
    return () => customerSearchDebounce.cancel();
  }, [customerSearchDebounce]);

  // handle inviting the selected customer to this (private) auction
  const handleInviteCustomer = () => {
    if (!inviteCustomer?.id) return;
    setInviteLoader(true);
    dispatch(createBidRequest({ customer: inviteCustomer.id, auction: id }))
      .then((res) => {
        if (res && res?.status === 201) {
          toast.success(t("information.customerInvited"));
          setInviteCustomer(null);
          handleGetBidRequests(payload);
        }
      })
      .finally(() => setInviteLoader(false));
  };

  // handle getting bid requests
  const handleGetBidRequests = useCallback(
    (payload) => {
      setTableLoader(true);
      dispatch(getBidRequests(payload)).then((res) => {
        if (res) {
          setTableData({
            data: res?.data?.data?.results,
            total: res?.data?.data?.count,
          });
        }
        setTableLoader(false);
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (isModalOpen) {
      handleGetBidRequests(payload);
    }
  }, [handleGetBidRequests, isModalOpen, payload]);

  const handleModalClose = () => {
    onCancel();
  };

  // handle page change
  const handlePageChange = (value) => {
    setPayload((prevState) => ({
      ...prevState,
      page: value,
    }));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (value) => {
    setPayload((prevState) => ({
      ...prevState,
      limit: value,
    }));
  };

  //   approve bid
  const handleBidRequestStatusUpdate = (bidRequestId, requestType) => {
    dispatch(
      updateBidRequestStatus(bidRequestId, {
        request_status: requestType,
      })
    ).then((res) => {
      if (res) {
        setTableData((prevState) => {
          const updatedData = [...prevState?.data];
          const bidIndex = updatedData?.findIndex((item) => item?.id === bidRequestId);
          if (bidIndex >= 0) {
            updatedData[bidIndex].request_status = requestType;
          }
          return { ...prevState, data: updatedData };
        });
      }
    });
  };

  const columns = [
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{`${t("customers.customer")} ${t("common.no_")}`}</div>
          <CustomSearchInput
            inputType="number"
            columnWise={true}
            columnSearchKey={"customer__customer_no"}
            className="column-search-input"
            payload={payload}
            setPayload={setPayload}
          />
        </div>
      ),
      selector: (row) => (row?.customer?.customer_no ? row?.customer?.customer_no : "-"),
      width: "25%",
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{`${t("common.customer")}`}</div>
          <CustomSearchInput
            columnWise={true}
            columnSearchKey={"search_name__icontains"}
            className="column-search-input"
            payload={payload}
            setPayload={setPayload}
          />
        </div>
      ),
      selector: (row) => (row?.customer?.id ? getOnlyCustomerName(row?.customer) : "-"),
      width: "50%",
    },
    {
      name: (
        <div className="table-actions font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{t("common.actions")}</div>
        </div>
      ),
      selector: (row) => (
        <div className="table-actions">
          {row?.request_status ? (
            <CustomTagChip
              bgColor={row?.request_status === "approved" ? "green" : "red"}
              textColor={"white"}
              title={row?.request_status === "approved" ? t("common.approved") : t("common.rejected")}
            />
          ) : (
            <>
              <ActionButton
                iconColor={"green"}
                iconClasses="ri-checkbox-circle-line fs-24"
                buttonId={`tooltip-${row?.id}-copy`}
                tooltip={t("common.approve")}
                onClick={() => handleBidRequestStatusUpdate(row?.id, "approved")}
              />
              <ActionButton
                iconColor={"red"}
                iconClasses="ri-close-circle-line fs-24"
                buttonId={`tooltip-${row?.id}-edit`}
                tooltip={t("common.reject")}
                onClick={() => handleBidRequestStatusUpdate(row?.id, "rejected")}
              />
            </>
          )}
        </div>
      ),
      width: "25%",
      center: true,
    },
  ];

  return (
    <Modal centered isOpen={isModalOpen} size="lg" toggle={handleModalClose} fade={false}>
      <ModalHeader className="lot-header w-100">
        <div className="d-flex w-100 flex-row justify-content-between">
          <span>{t("common.bidRequests")}</span>
        </div>
      </ModalHeader>
      <ModalBody
        className="subcard-table-container table-light"
        style={{ border: "0px", borderRadius: "5px", boxShadow: "2px" }}
      >
        <Row className="mb-3 align-items-center">
          <Col>
            <Select
              isLoading={customerSearchLoader}
              isClearable
              name="inviteCustomer"
              options={customerSearchLoader ? [] : customerOptions}
              getOptionLabel={getCustomerOptionLabel}
              getOptionValue={(option) => option?.id}
              className="custom-select"
              placeholder={`${t("common.select")} ${t("common.customer")}`}
              value={inviteCustomer}
              onChange={(customer) => setInviteCustomer(customer?.id ? customer : null)}
              onInputChange={customerSearchDebounce}
            />
          </Col>
          <Col xs="auto">
            <Button
              color="primary"
              type="button"
              disabled={!inviteCustomer?.id || inviteLoader}
              onClick={handleInviteCustomer}
            >
              {t("common.inviteCustomer")}
            </Button>
          </Col>
        </Row>
        <Card className="subcard-table-container table-light">
          {tableLoader ? <Loader /> : null}
          <DataTable
            fixedHeader
            pointerOnHover
            className="data-table"
            persistTableHead
            columns={columns}
            data={[...tableData?.data]}
            pagination
            paginationServer
            paginationDefaultPage={payload?.page}
            paginationPerPage={payload?.limit}
            paginationTotalRows={tableData?.total}
            noDataComponent={<NoRecords />}
            onChangeRowsPerPage={handleRowsPerPageChange}
            onChangePage={handlePageChange}
            paginationComponentOptions={{
              rowsPerPageText: t("common.rowsPerPage"),
              rangeSeparatorText: t("common.rangeSeparator"),
            }}
            conditionalRowStyles={[
              {
                when: (row) => row?.unsold_item_count === 0,
                style: { backgroundColor: "lightgray" },
              },
            ]}
          />
        </Card>
      </ModalBody>
    </Modal>
  );
};

export default withTranslation()(BidRequestsModal);
